<template>
 <header
    v-if="props.isPlpReady"
    class="flex items-center mb-2 text-[#555] font-['Visuelt_Pro'] text-[14px] font-[400] leading-[120%] tracking-[-0.035px]"
    role="banner"
    aria-label="Taia guided results"
  >
    <nav aria-label="Breadcrumb">
      <ol class="flex flex-wrap items-center gap-2 pl-[25px] md:pl-0">
        <li class="flex items-center">
          <button type="button" class="cursor-pointer" aria-label="Go back to Taia guided results" @click="goToPreviousPage">
            <Icon icon="arrow-left" class="h-4.42 w-4.42 fill-current text-current" />
          </button>
        </li>
        <li v-if="!displaySelectedFilters.length" class="flex items-center">
          <span aria-hidden="true">/</span>
          <span class="sr-only">Taia guided results</span>
        </li>
        <template v-else v-for="(filter, index) in displaySelectedFilters" :key="`${filter}-${index}`">
          <li class="flex items-center">
            <span aria-hidden="true">/</span>
            <button
              v-if="filter === revertibleFilter"
              type="button"
              class="mx-1 cursor-pointer underline underline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="props.isFiltering"
              @click="emit('revert-filter', revertibleSelectedFilter ? filter : undefined)"
            >
              {{ filter }}
            </button>
            <span
              v-else
              class="mx-1"
              :class="{ 'text-[#555]': index === displaySelectedFilters.length - 1 }"
              :aria-current="index === displaySelectedFilters.length - 1 ? 'page' : undefined"
            >{{ filter }}</span>
          </li>
        </template>
      </ol>
    </nav>
  </header>
  <div class="flex flex-col items-start gap-8 md:flex-row">
    <AgenticRecommendationBanner
      :content="props.response.content"
      :available-filters="props.response.facet_filters"
      :selected-filters="props.selectedFilters"
      :product-count="props.response.product_count"
      :is-filtering="props.isFiltering"
      :is-plp-ready="props.isPlpReady"
      :show-product-grid="props.showProductGrid"
      :is-compare-mode="isCompareMode"
      :product-data="props.response.products"
      @select-filter="(filterKey) => emit('select-filter', filterKey)"
      @compare-products="emit('compare-products')"
    />

    <div class="flex-1 min-w-0 w-full">
      <template v-if="props.isPlpReady && props.showContent">
        <AgenticProductList :productData="renderedResponse" />
        <div
          v-if="hasMoreProducts"
          ref="loadMoreSentinel"
          class="h-px w-full"
          aria-hidden="true"
        />
      </template>
      <div v-else class="flex flex-1 flex-col items-start self-stretch rounded-[16px] border border-[#EAEAEA] bg-white p-4 md:p-6">
        <div
          class="grid w-full grid-cols-2 gap-3 lg:grid-cols-3"
          aria-busy="true"
          aria-live="polite"
        >
          <span class="sr-only">Content is loading</span>
          <div
            v-for="i in 6"
            :key="i"
            class="animate-pulse"
            role="status"
            aria-label="Loading product"
          >
            <div class="relative aspect-square w-full rounded-[0.571rem] bg-[#EAEAEA80]"></div>
            <div class="mt-2 h-8 w-full rounded bg-[#EAEAEA80]"></div>
            <div class="mt-1 h-3 w-full rounded bg-[#EAEAEA80]"></div>
            <div class="mt-1 h-3 w-3/6 rounded bg-[#EAEAEA80]"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import AgenticRecommendationBanner from '~/components/ui/organism/product/agentic/agenticRecommendationBanner.vue'
import AgenticProductList from '~/components/ui/organism/product/agentic/agenticProductList.vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import { useRouter } from 'vue-router'
import Icon from '~/components/ui/atom/icon.vue'
import type { FilterQueryResponse } from '~~/shared/types/common'

const INITIAL_VISIBLE_PRODUCTS = 10
const PRODUCTS_BATCH_SIZE = 10
import { useUserStore } from "@/stores/userStore";
import eventBusGtm from '~/composables/eventBusGtm';
import { useIframeStore } from '~/stores/chatAIStore'
import { storeToRefs } from 'pinia'
const iframeStore = useIframeStore()
const { isDynamicYieldVariationData } = storeToRefs(iframeStore);
const userStore = useUserStore();

const props = defineProps<{
  response: FilterQueryResponse,
  initialFilter?: string,
  selectedFilters: string[],
  isFiltering: boolean,
  isPlpReady: boolean,
  showProductGrid: boolean,
  showContent: boolean
}>()

const emit = defineEmits<{
  (e: 'select-filter', filterKey: string): void
  (e: 'revert-filter', filterKey?: string): void
  (e: 'compare-products'): void
}>()

const isDesktop = useBreakpoints(breakpointsTailwind).greaterOrEqual('lg')

const isCompareMode = computed(() => {
  return isDesktop.value && props.response.is_compare && props.response.product_count > 1 && props.response.product_count <= 5
})

const normalizeFilterValue = (filter: string) => {
  try {
    return decodeURIComponent(filter.replace(/\+/g, ' ')).trim()
  } catch {
    return filter.replace(/\+/g, ' ').trim()
  }
}

const displaySelectedFilters = computed(() => [...new Set([
  props.initialFilter ?? '',
  ...normalizedSelectedFilters.value
]
  .flatMap(filter => filter.split(','))
  .map(normalizeFilterValue)
  .filter(Boolean))])

const normalizedSelectedFilters = computed(() => props.selectedFilters
  .flatMap(filter => filter.split(','))
  .map(normalizeFilterValue)
  .filter(Boolean)
)

const revertibleSelectedFilter = computed(() => normalizedSelectedFilters.value.at(-2))
const revertibleFilter = computed(() => revertibleSelectedFilter.value
  ?? (normalizedSelectedFilters.value.length === 1
    ? normalizeFilterValue(props.initialFilter ?? '')
    : undefined))

const router = useRouter()

const goToPreviousPage = () => {
  const customerId = userStore.data?.customerId ?? null;
    eventBusGtm.emit("trackEvent", {
      event_name: "Agenticplp",
      button_name: "click_icon_return_plp",
      screen_name: "Agenticplp_results",
      Customer_id: customerId,
      message_id: null,
      Selected_thread_id: null,
      target: null,
      productName: null,
      productSku: null,
      productPrice: null,
      skuid: null,
      sub_screen: null,
      article: null,
      Writing_style_preference: null,
      Content_preference: null,
      ResponseDetail_preference: null,
      Taia_campaign: null,
      search_term: null,
      variationName: isDynamicYieldVariationData.value?.name ?? null,
      experienceName: isDynamicYieldVariationData.value?.experienceName ?? null,
    });
  router.back()
}

const visibleProductCount = ref(INITIAL_VISIBLE_PRODUCTS)
const loadMoreSentinel = ref<HTMLElement | null>(null)
const observer = ref<IntersectionObserver | null>(null)
const isLoadingMore = ref(false)
const hasUserScrolled = ref(false)

const totalProducts = computed(() => props.response.products.length)

const displayedProducts = computed(() => {
  return props.response.products.slice(0, visibleProductCount.value)
})

const renderedResponse = computed<FilterQueryResponse>(() => ({
  ...props.response,
  products: displayedProducts.value
}))

const hasMoreProducts = computed(() => visibleProductCount.value < totalProducts.value)

const disconnectObserver = () => {
  observer.value?.disconnect()
  observer.value = null
}

const markUserScrolled = () => {
  if (!hasUserScrolled.value) {
    hasUserScrolled.value = true
    if (observer.value) {
      setupObserver()
    }
  }
}

const loadNextBatch = () => {
  if (isLoadingMore.value || !hasMoreProducts.value) {
    return
  }

  isLoadingMore.value = true
  visibleProductCount.value = Math.min(
    visibleProductCount.value + PRODUCTS_BATCH_SIZE,
    totalProducts.value
  )
  isLoadingMore.value = false
}

const setupObserver = () => {
  if (!(process as any).client || !loadMoreSentinel.value || !hasMoreProducts.value) {
    return
  }

  disconnectObserver()

  observer.value = new IntersectionObserver(
    (entries) => {
      const [entry] = entries
      if (entry?.isIntersecting && hasUserScrolled.value) {
        loadNextBatch()
      }
    },
    {
      root: null,
      rootMargin: '200px 0px',
      threshold: 0
    }
  )

  observer.value.observe(loadMoreSentinel.value)
}

watch(loadMoreSentinel, (element) => {
  if (element) {
    setupObserver()
  } else {
    disconnectObserver()
  }
}, { flush: 'post' })

watch(
  () => [props.response.filter_state, props.response.products],
  async () => {
    visibleProductCount.value = INITIAL_VISIBLE_PRODUCTS
    hasUserScrolled.value = false
    await nextTick()
    setupObserver()
  },
  { deep: true }
)

watch(hasMoreProducts, (hasMore) => {
  if (!hasMore) {
    disconnectObserver()
  }
})

onMounted(async () => {
  if ((process as any).client) {
    window.addEventListener('scroll', markUserScrolled, { passive: true })
  }
  await nextTick()
  setupObserver()
})

onBeforeUnmount(() => {
  disconnectObserver()
  if ((process as any).client) {
    window.removeEventListener('scroll', markUserScrolled)
  }
})
</script>